const SITE_URL = "https://compelion.com";
const DEFAULT_IMAGE_URL = `${SITE_URL}/logo.png`;
const SCHEMA_ID_PREFIX = "compelion-schema";

const ensureMetaTag = (attribute, value) => {
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  return element;
};

const ensureLinkTag = (rel) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  return element;
};

const ensureHreflangLink = (hreflang) => {
  let element = document.head.querySelector(
    `link[rel="alternate"][hreflang="${hreflang}"]`,
  );
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "alternate");
    element.setAttribute("hreflang", hreflang);
    document.head.appendChild(element);
  }
  return element;
};

const clearHreflangLinks = () => {
  const existing = document.head.querySelectorAll(
    'link[rel="alternate"][hreflang]',
  );
  existing.forEach((element) => element.remove());
};

const clearStructuredData = () => {
  const existing = document.head.querySelectorAll(
    `script[data-seo-schema^="${SCHEMA_ID_PREFIX}"]`,
  );
  existing.forEach((element) => element.remove());
};

/**
 * Strips the /ar/ prefix from a path to get the base English path.
 */
export const getBasePathFromArabic = (pathname) => {
  if (pathname.startsWith("/ar/")) {
    return pathname.slice(3) || "/";
  }
  if (pathname === "/ar") {
    return "/";
  }
  return pathname;
};

/**
 * Returns true when the current pathname is an Arabic route.
 */
export const isArabicPath = (pathname) =>
  pathname === "/ar" || pathname.startsWith("/ar/");

export const normalizeSeoDescription = (text, maxLength = 160) => {
  if (!text) {
    return "";
  }

  const plainText = text
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (plainText.length <= maxLength) {
    return plainText;
  }

  return `${plainText.slice(0, maxLength - 1).trim()}...`;
};

export const applySeo = ({
  title,
  description,
  canonicalPath,
  robots = "index,follow",
  ogType = "website",
  imageUrl = DEFAULT_IMAGE_URL,
  structuredData = [],
  lang = "en",
  alternatePaths = null,
}) => {
  const resolvedCanonicalUrl = canonicalPath
    ? `${SITE_URL}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`
    : null;

  document.title = title;

  /* lang & dir on <html> */
  document.documentElement.setAttribute("lang", lang);
  document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");

  ensureMetaTag("http-equiv", "content-language").setAttribute("content", lang);
  ensureMetaTag("name", "description").setAttribute("content", description);
  ensureMetaTag("name", "robots").setAttribute("content", robots);
  ensureMetaTag("property", "og:title").setAttribute("content", title);
  ensureMetaTag("property", "og:description").setAttribute(
    "content",
    description,
  );
  ensureMetaTag("property", "og:type").setAttribute("content", ogType);
  ensureMetaTag("property", "og:image").setAttribute("content", imageUrl);
  ensureMetaTag("property", "og:site_name").setAttribute(
    "content",
    "Compelion",
  );
  ensureMetaTag("property", "og:url").setAttribute(
    "content",
    resolvedCanonicalUrl || SITE_URL,
  );
  ensureMetaTag("property", "og:locale").setAttribute(
    "content",
    lang === "ar" ? "ar_AE" : "en_GB",
  );
  ensureMetaTag("name", "twitter:card").setAttribute(
    "content",
    "summary_large_image",
  );
  ensureMetaTag("name", "twitter:title").setAttribute("content", title);
  ensureMetaTag("name", "twitter:description").setAttribute(
    "content",
    description,
  );
  ensureMetaTag("name", "twitter:image").setAttribute("content", imageUrl);

  const canonicalLink = ensureLinkTag("canonical");
  canonicalLink.setAttribute("href", resolvedCanonicalUrl || SITE_URL);

  /* Hreflang alternate links */
  clearHreflangLinks();
  if (alternatePaths) {
    ensureHreflangLink("en").setAttribute(
      "href",
      `${SITE_URL}${alternatePaths.en}`,
    );
    ensureHreflangLink("ar").setAttribute(
      "href",
      `${SITE_URL}${alternatePaths.ar}`,
    );
    ensureHreflangLink("x-default").setAttribute(
      "href",
      `${SITE_URL}${alternatePaths.en}`,
    );
  }

  clearStructuredData();
  structuredData.forEach((entry, index) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seoSchema = `${SCHEMA_ID_PREFIX}-${index}`;
    script.textContent = JSON.stringify(entry);
    document.head.appendChild(script);
  });
};

export const applyNoIndexSeo = (title = "Compelion") => {
  clearStructuredData();
  clearHreflangLinks();
  document.title = title;
  document.documentElement.setAttribute("lang", "en");
  document.documentElement.setAttribute("dir", "ltr");
  ensureMetaTag("name", "robots").setAttribute("content", "noindex,nofollow");
  ensureMetaTag("name", "description").setAttribute(
    "content",
    "Compelion innovation management platform.",
  );
  ensureLinkTag("canonical").setAttribute("href", SITE_URL);
};

export const SEO_CONSTANTS = {
  SITE_URL,
  DEFAULT_IMAGE_URL,
};
